/**
 * Application configuration
 * Environment-driven configuration for seamless local → cloud migration
 */

export const config = {
    // API Configuration
    apiUrl: import.meta.env.VITE_API_URL || '',
    apiTimeout: 30000,

    // Environment
    environment: import.meta.env.MODE || 'development',
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
    isDemo: import.meta.env.VITE_DEMO_MODE === 'true',

    // Authentication
    tokenKey: 'digestr_token',
    refreshTokenKey: 'digestr_refresh_token',
    tokenExpiration: 24 * 60 * 60 * 1000, // 24 hours

    // External Services
    stripePublicKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY || '',

    // Feature Flags
    features: {
        darkMode: true,
        notifications: true,
        analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
        errorReporting: import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true',
        isPrelaunch: false
    },

    // UI Configuration
    ui: {
        defaultTheme: 'light' as 'light' | 'dark' | 'system',
        pageSize: 20,
        maxFileSize: 10 * 1024 * 1024, // 10MB
        supportedImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
    },

    // App Metadata
    app: {
        name: 'Digestr',
        version: import.meta.env.VITE_APP_VERSION || '0.1.0',
        description: 'AI-powered briefings for busy professionals',
        url: import.meta.env.VITE_APP_URL || 'http://localhost:5173',
    },

    // Demo Configuration
    demo: {
        email: 'demo@digestr.ai',
        password: 'demo123456',
        resetInterval: 60 * 60 * 1000, // 1 hour
    },
} as const

// Type for environment variables (for better TypeScript support)
export type Config = typeof config

// Validation function to ensure required environment variables are set
export function validateConfig(): void {
    const requiredEnvVars: string[] = [
        // Add required environment variables here
    ]

    const missingVars = requiredEnvVars.filter(
        (varName) => !import.meta.env[varName]
    )

    if (missingVars.length > 0) {
        throw new Error(
            `Missing required environment variables: ${missingVars.join(', ')}`
        )
    }
}

// Log configuration in development
if (config.isDevelopment) {
    console.log('🔧 App Configuration:', {
        environment: config.environment,
        apiUrl: config.apiUrl,
        isDemo: config.isDemo,
        version: config.app.version,
    })
}